/**
 * Ultra-HD QR generation for PDFs and print surfaces.
 * Raster PDFs blur soft JPEG captures — QR must be a large crisp PNG
 * with high error correction so scanners still work after zoom/print.
 */
import QRCode from 'qrcode';

export type HdQrOptions = {
  /** Output pixel size (square). Prefer ≥ 768 for PDF embed. */
  size?: number;
  /** Quiet-zone modules around the code. */
  margin?: number;
};

/** Build a lossless PNG data URL for `payload` (UPI string, doc ref, URL, …). */
export async function generateHdQrDataUrl(
  payload: string,
  opts?: HdQrOptions
): Promise<string> {
  const text = String(payload || '').trim();
  if (!text) throw new Error('QR payload is empty.');
  const size = Math.max(512, Math.min(2048, Math.round(opts?.size ?? 1024)));
  const margin = opts?.margin ?? 2;
  return QRCode.toDataURL(text, {
    errorCorrectionLevel: 'H',
    type: 'image/png',
    margin,
    width: size,
    rendererOpts: { quality: 1 },
    color: {
      dark: '#000000',
      light: '#FFFFFF',
    },
  });
}

/** CSS snippet so html2canvas keeps QR modules sharp (no soft resampling). */
export const HD_QR_IMG_STYLE =
  'image-rendering:pixelated;image-rendering:crisp-edges;-ms-interpolation-mode:nearest-neighbor;';
